import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindProductBrandValuesQueryResponse,
  FindProductBrandValuesQueryParams,
} from "../types/FindProductBrandValues"

type FindProductBrandValuesClient = typeof client<
  FindProductBrandValuesQueryResponse,
  Error,
  never
>
type FindProductBrandValues = {
  data: FindProductBrandValuesQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindProductBrandValuesQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindProductBrandValuesClient>>
  client: {
    parameters: Partial<Parameters<FindProductBrandValuesClient>[0]>
    return: Awaited<ReturnType<FindProductBrandValuesClient>>
  }
}
export const findProductBrandValuesQueryKey = (
  params: FindProductBrandValues["queryParams"]
) =>
  [
    { url: "/v1/product/find-product-brand-values" },
    ...(params ? [params] : []),
  ] as const
export type FindProductBrandValuesQueryKey = ReturnType<
  typeof findProductBrandValuesQueryKey
>
export function findProductBrandValuesQueryOptions<
  TData = FindProductBrandValues["response"],
  TQueryData = FindProductBrandValues["response"],
>(
  params: FindProductBrandValues["queryParams"],
  options: FindProductBrandValues["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindProductBrandValues["response"],
    FindProductBrandValues["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findProductBrandValuesQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindProductBrandValues["data"],
        FindProductBrandValues["error"]
      >({
        method: "get",
        url: `/v1/product/find-product-brand-values`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find product brand values
 * @link /v1/product/find-product-brand-values
 */
export function useFindProductBrandValues<
  TData = FindProductBrandValues["response"],
  TQueryData = FindProductBrandValues["response"],
  TQueryKey extends QueryKey = FindProductBrandValuesQueryKey,
>(
  params: FindProductBrandValues["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindProductBrandValues["response"],
        FindProductBrandValues["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindProductBrandValues["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindProductBrandValues["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey =
    queryOptions?.queryKey ?? findProductBrandValuesQueryKey(params)
  const query = useQuery<
    FindProductBrandValues["data"],
    FindProductBrandValues["error"],
    TData,
    any
  >({
    ...findProductBrandValuesQueryOptions<TData, TQueryData>(
      params,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindProductBrandValues["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
